<template>
  <header :class="['fixed top-0 left-0 right-0 z-[300] transition-all duration-300', scrolled ? 'bg-white/95 backdrop-blur-sm shadow-md' : 'bg-transparent']">
    <div class="container mx-auto px-4 lg:px-8">
      <nav class="flex items-center justify-between h-20">
        <!-- Logo -->
        <router-link to="/" class="flex items-center gap-3 group">
          <div class="w-10 h-10 bg-[#0056b3] rounded-xl flex items-center justify-center transition-transform group-hover:scale-105">
            <span class="text-white font-bold text-xl">X</span>
          </div>
          <div class="flex flex-col">
            <span class="text-xl font-bold text-[#2d3748] leading-tight">{{ $t('logoText') }}</span>
            <span class="text-xs font-semibold text-[#0056b3] tracking-wider">XIONGSU</span>
          </div>
        </router-link>

        <!-- Desktop Nav -->
        <div class="hidden lg:flex items-center gap-8">
          <ul class="flex items-center gap-6">
            <li v-for="link in navLinks" :key="link.to">
              <router-link :to="link.to" class="nav-link text-[#4a5568] font-medium hover:text-[#0056b3] transition-colors relative py-2">
                {{ $t(link.label) }}
              </router-link>
            </li>
          </ul>
        </div>

        <!-- Right: Lang + CTA -->
        <div class="hidden lg:flex items-center gap-4">
          <LanguageSwitcher />
          <router-link to="/contact" class="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0056b3] text-white rounded-lg font-medium hover:bg-[#003d80] transition-colors text-sm">
            {{ $t('navCTA') }}
          </router-link>
        </div>

        <!-- Mobile Menu Button -->
        <button @click="mobileOpen = !mobileOpen" class="lg:hidden p-2 rounded-lg text-[#4a5568] hover:bg-[#f7fafc]">
          <X v-if="mobileOpen" class="w-6 h-6" />
          <Menu v-else class="w-6 h-6" />
        </button>
      </nav>
    </div>

    <!-- Mobile Menu -->
    <transition name="slide">
      <div v-if="mobileOpen" class="lg:hidden fixed inset-x-0 top-[80px] bottom-0 bg-white z-[299] overflow-y-auto">
        <div class="container mx-auto px-4 py-6">
          <!-- Mobile Language -->
          <div class="flex items-center justify-center gap-2 mb-6 pb-6 border-b border-[#e2e8f0]">
            <button v-for="lang in mobileLangs" :key="lang.code" @click="switchLang(lang.code)"
              :class="['px-4 py-2 rounded-lg text-sm font-medium transition-all', locale === lang.code ? 'bg-[#0056b3] text-white' : 'bg-[#f7fafc] text-[#4a5568]']">
              {{ lang.label }}
            </button>
          </div>
          <ul class="space-y-1">
            <li v-for="link in navLinks" :key="link.to">
              <router-link :to="link.to" @click="mobileOpen = false"
                class="flex items-center gap-3 px-4 py-3 rounded-xl text-[#4a5568] hover:bg-[#e6f0fa] hover:text-[#0056b3] transition-all font-medium">
                {{ $t(link.label) }}
              </router-link>
            </li>
          </ul>
          <div class="mt-6 pt-6 border-t border-[#e2e8f0]">
            <router-link to="/contact" @click="mobileOpen = false"
              class="block w-full text-center px-5 py-3 bg-[#0056b3] text-white rounded-lg font-medium">
              {{ $t('navCTA') }}
            </router-link>
          </div>
        </div>
      </div>
    </transition>
  </header>
  <div class="h-20"></div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { Menu, X } from 'lucide-vue-next'
import LanguageSwitcher from './LanguageSwitcher.vue'

const { locale } = useI18n()
const scrolled = ref(false)
const mobileOpen = ref(false)

const navLinks = [
  { to: '/', label: 'navHome' },
  { to: '/products', label: 'navProducts' },
  { to: '/about', label: 'navAbout' },
  { to: '/bases', label: 'navBases' },
  { to: '/quality', label: 'navQuality' },
  { to: '/news', label: 'navNews' },
  { to: '/contact', label: 'navContact' }
]

const mobileLangs = [
  { code: 'zh-CN', label: '中文' },
  { code: 'en', label: 'English' },
  { code: 'ar', label: 'العربية' }
]

function switchLang(code) { locale.value = code }

function onScroll() { scrolled.value = window.scrollY > 100 }

onMounted(() => window.addEventListener('scroll', onScroll, { passive: true }))
onUnmounted(() => window.removeEventListener('scroll', onScroll))
</script>

<style scoped>
.nav-link::after {
  content: ''; position: absolute; bottom: 0; left: 0; width: 100%; height: 2px;
  background-color: #0056b3; transform: scaleX(0); transition: transform 0.3s ease;
}
.nav-link:hover::after, .router-link-exact-active::after { transform: scaleX(1); }
.router-link-exact-active { color: #0056b3; }
.slide-enter-active, .slide-leave-active { transition: transform 0.3s ease; }
.slide-enter-from, .slide-leave-to { transform: translateX(100%); }
</style>
